import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { updateTicketSubject } from 'flow/actions/tickets-action';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { TicketKey } from 'flow/types/EntityKeys';
import {
    selectOriginDTOSubtype,
    selectSubject,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { twMerge } from 'tailwind-merge';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useTicketAction from 'flow/hooks/useTicketAction';

interface Props {
    ticketKey: TicketKey;
}

const SubjectInput = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    // Only to explain the block, not to apply it: setSubject already accounts for an
    // email ticket's subject coming from the mail header. The resolved permissions
    // carry no reasons, so the reason stays here.
    const dtoSubtypeIsEmail =
        useAppSelector((state) => selectOriginDTOSubtype(state, ticketKey)) ===
        'email';
    const subjectFromStore = useAppSelector((state) =>
        selectSubject(state, ticketKey),
    );
    const canSetSubject = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'setSubject'),
    );
    const [subjectToDisplay, setSubjectToDisplay] = useState('');
    const [isPerformingRequest, setIsPerformingRequest] = useState(false);
    const [hasSentSegmentOnInputChange, setHasSentSegmentOnInputChange] =
        useState(false);

    useEffect(() => {
        setSubjectToDisplay(subjectFromStore);
    }, [subjectFromStore]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!hasSentSegmentOnInputChange) {
            dispatch(
                segmentTrack(TICKET_STRINGS.USER_BEGAN_WRITING_SUBJECT, {
                    component: 'ticket details overview',
                }),
            );
            setHasSentSegmentOnInputChange(true);
        }
        setSubjectToDisplay(event.target.value);
    };

    const handleSaveClick = useCallback(() => {
        dispatch(
            segmentTrack(TICKET_STRINGS.SUBJECT_PUBLISHED, {
                component: 'ticket details overview',
            }),
        );
        setIsPerformingRequest(true);

        runTicketAction(
            updateTicketSubject(ticketKey, subjectToDisplay),
        ).finally(() => {
            setIsPerformingRequest(false);
        });
    }, [dispatch, subjectToDisplay, ticketKey]);

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            handleSaveClick();
        }
    };

    const isDisabled = isPerformingRequest || !canSetSubject;

    const inputElement = (
        <div className="relative flex w-full">
            <input
                type="text"
                aria-label={i18next.t('ticket.enter.subject')}
                value={subjectToDisplay}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={twMerge(
                    'h-10 w-full rounded-sm bg-gray-200 ps-3 pe-16 text-sm focus:outline-hidden',
                    isDisabled ? 'text-gray-500' : '',
                )}
                placeholder={i18next.t('ticket.enter.subject')}
                disabled={isDisabled}
            />
            {subjectToDisplay !== subjectFromStore && canSetSubject ? (
                <button
                    onClick={handleSaveClick}
                    className={twMerge(
                        'darken-5 absolute end-1 top-1 flex h-7 items-center justify-center rounded-sm border bg-gray-200 px-3 text-xs text-black',
                        isPerformingRequest
                            ? 'text-gray-300'
                            : 'hover:bg-gray-300',
                    )}
                    disabled={isPerformingRequest}
                >
                    {i18next.t('navigate.save')}
                </button>
            ) : null}
        </div>
    );

    return (
        <div>
            <div className="mb-2 text-xs font-bold text-black">
                {i18next.t('ticket.subject')}
            </div>
            {dtoSubtypeIsEmail ? (
                <Tooltip
                    label={i18next.t('ticket.subject.email.cannot_change')}
                >
                    {inputElement}
                </Tooltip>
            ) : (
                inputElement
            )}
        </div>
    );
};

export default SubjectInput;
